/**
 * P5 W4 — Auth store (Zustand + localStorage persist)。
 *
 * 持久化 access_token + staff info；refresh_token 在 httpOnly cookie 由后端管。
 * configureApiClient 在 main.tsx 启动时调用，注入 token getter / 401 callback。
 */
import { create } from "zustand"
import { persist } from "zustand/middleware"
import { apiClient, configureApiClient } from "@/lib/api-client"

export interface StaffPublicInfo {
  id: string
  email: string
  role: "admin" | "operator" | "advisor"
  name_display: string
  must_change_password: boolean
}

interface AuthState {
  accessToken: string | null
  staff: StaffPublicInfo | null
  isLoading: boolean
  loginError: string | null

  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  refreshMe: () => Promise<void>
  setAccessToken: (token: string) => void
  clear: () => void
}

function messageFromApiDetail(
  detail: unknown,
  fallback = "登录失败，请检查邮箱和密码",
) {
  if (typeof detail === "string" && detail.trim()) {
    return detail
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") return item
        if (item && typeof item === "object" && "msg" in item) {
          const msg = (item as { msg?: unknown }).msg
          return typeof msg === "string" ? msg : ""
        }
        return ""
      })
      .filter(Boolean)
    return messages.length > 0 ? messages.join("；") : fallback
  }
  if (detail && typeof detail === "object" && "msg" in detail) {
    const msg = (detail as { msg?: unknown }).msg
    return typeof msg === "string" && msg.trim() ? msg : fallback
  }
  return fallback
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      staff: null,
      isLoading: false,
      loginError: null,

      async login(email: string, password: string) {
        set({ isLoading: true, loginError: null })
        try {
          const resp = await apiClient.post("/staff/auth/login", { email, password })
          set({
            accessToken: resp.data.access_token,
            staff: resp.data.staff,
            isLoading: false,
            loginError: null,
          })
          return true
        } catch (err: unknown) {
          const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
          const msg = messageFromApiDetail(detail)
          set({ isLoading: false, loginError: msg })
          return false
        }
      },

      async logout() {
        try {
          await apiClient.post("/staff/auth/logout")
        } catch {
          // 忽略 logout 失败（已 401 也算成功）
        }
        get().clear()
      },

      async refreshMe() {
        try {
          const resp = await apiClient.get("/staff/auth/me")
          set({ staff: resp.data })
        } catch {
          // me 失败 → 通常 401 已被 interceptor 处理
        }
      },

      setAccessToken(token: string) {
        set({ accessToken: token })
      },

      clear() {
        set({ accessToken: null, staff: null, loginError: null })
      },
    }),
    {
      name: "cue-staff-auth",
      partialize: (state) => ({
        accessToken: state.accessToken,
        staff: state.staff,
      }),
    },
  ),
)

// 启动时注入 api-client 拦截器需要的 callbacks
configureApiClient({
  getAccessToken: () => useAuthStore.getState().accessToken,
  onAuthFailure: () => useAuthStore.getState().clear(),
  onTokenRefresh: (newToken) => useAuthStore.getState().setAccessToken(newToken),
})
